import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { SubmitButton } from '../common/forms/submitButton'
import { IntegerField } from '../common/forms/integerField'
import * as Yup from 'yup'
import { Field, Form, Formik } from 'formik'
import QRButton from '../common/forms/QRButton'
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Link } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { DevolucionesPopover, useDevolucionesPopover } from "../../components/albaranes/DevolucionesPopover";

// Margen que aún se puede devolver. Nunca negativo: si el albarán llega con más
// devoluciones informadas que entregas, no se marca la línea hasta que el usuario
// introduzca una devolución.
const margenDevolucion = (detalle) =>
  Math.max(
    Number(detalle.ejemplares_venta ?? 0) - Number(detalle.devoluciones_informadas ?? 0),
    0
  )

const excedeEntregas = (detalle) =>
  Number(detalle.devoluciones ?? 0) > margenDevolucion(detalle)

export const AlbaranForm = (props) => {
  const devolucionesPopover = useDevolucionesPopover();

  const initialFormValues = {
    detalles: props.detalles.map((detalle) => ({
      id: detalle.id,
      nombre_articulo: detalle.nombre_articulo,
      ejemplares_venta: detalle.ejemplares_venta || 0,
      devoluciones_informadas: detalle.devoluciones_informadas || 0,
      devoluciones_log: detalle.devoluciones_log || [],
      devoluciones: null,
    }))
  }

  const schema = Yup.object().shape({
    detalles: Yup.array().of(
      Yup.object({
        devoluciones: Yup.number()
          .nullable()
          .typeError('Debe ser un número')
          .min(0, 'La cantidad debe ser 0 o más')
          .max(999, 'No puedes superar 999 unidades')
      })
    )
    .test('at-least-one-devolucion', 'Debes introducir al menos una devolución', (detalles) => {
      return detalles.some(detalle => detalle.devoluciones > 0);
    })
    .test('too-many-devoluciones', 'No puede haber más devoluciones que entregas', (detalles) => {
      return detalles.every(detalle => !excedeEntregas(detalle));
    })
  });

  return (
    <Formik
      initialValues={initialFormValues}
      onSubmit={props.handleSubmit}
      validationSchema={schema}
    >
      {({ handleChange, values, isSubmitting, isValid, touched, errors, setFieldValue }) => (
        <Box
          component={Form}
          autoComplete='off'
        >
          <TableContainer component={Paper} sx={{ maxHeight: 450, overflowY: 'auto' }}>
            <Table stickyHeader sx={{ 
              '& .MuiTableCell-root': { 
                px: 0.75,
                py: 1.5,
              },
              '& .MuiTableCell-root.MuiTableCell-head': { 
                py: 1,
              } 
            }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '70%' }}>Publicación</TableCell>
                <TableCell sx={{ width: '10%' }} align="center">Entrega</TableCell>
                <TableCell sx={{ width: '10%' }} align="center">Devol. inform. </TableCell>
                <TableCell sx={{ width: '10%' }} align="center">Devolución</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              { values.detalles.map((detalle, index) => {
                const excede = excedeEntregas(detalle);

                return (
                <TableRow
                  key={detalle.id}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    ...(excede && {
                      bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                      '& .MuiTableCell-root': { color: 'error.main' },
                    }),
                  }}
                >
                  <TableCell sx={{ fontSize: '0.8rem' }}>{ detalle.nombre_articulo }</TableCell>
                  <TableCell align="center">{ detalle.ejemplares_venta || 0 }</TableCell>
                  <TableCell align="center">
                    {detalle.devoluciones_log?.length > 0 ? (
                      <Box
                        component="span"
                        onClick={(event) => devolucionesPopover.toggle(event, detalle.devoluciones_log)}
                        sx={{
                          display: 'inline-flex',
                          cursor: 'pointer',
                          borderBottom: '1px dotted currentColor',
                          color: 'info.main',
                        }}
                      >
                        { detalle.devoluciones_informadas || 0 }
                      </Box>
                    ) : (
                      detalle.devoluciones_informadas || 0
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IntegerField
                      name={`detalles[${index}].devoluciones`}
                      value={detalle.devoluciones}
                      onChange={(newValue) => {
                        setFieldValue(`detalles[${index}].devoluciones`, newValue);
                      }}
                      error={excede || (touched?.detalles?.[index]?.devoluciones && Boolean(errors?.detalles?.[index]?.devoluciones))}
                      helperText={touched?.detalles?.[index]?.devoluciones && errors?.detalles?.[index]?.devoluciones}
                    />
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <DevolucionesPopover
          open={devolucionesPopover.open}
          anchorEl={devolucionesPopover.anchorEl}
          onClose={devolucionesPopover.handleClose}
          items={devolucionesPopover.desglose}
        />
        { !isValid && typeof errors.detalles === 'string' && (
          <Box sx={{ color: 'error.main', my: 3 }}>
            { errors.detalles }
          </Box>
        ) }
          <Stack direction="row" spacing={1} sx={{ my: 3 }}>
            <SubmitButton
              text='Enviar'
              isValid={isValid}
              isSubmitting={isSubmitting}
            />
            <Button
              component={Link}
              to="/albaranes"
              variant="outlined"
              color="primary"
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          </Stack>
        </Box>
      )}
    </Formik>
  )
}
